<template>
  <form @submit.prevent="handleSubmit">
    <div class="form-field">
      <input-text
        elem-id="soAdditionalServiceFiscalLc116"
        v-model="formData.so_additional_service_fiscal_lc116_code"
        :label="t('Código LC116')"
        :placeholder="inheritedPlaceholder('so_additional_service_fiscal_lc116_code') || t('Ex: 14.01')"
        :validation="() => isValidLc116Code(formData.so_additional_service_fiscal_lc116_code)"
        required
        fluid
        data-testid="so-catalog-fiscal-additional-service-lc116-input"
      />
      <small v-if="errors.lc116_code" class="error-message">{{ errors.lc116_code }}</small>
      <small
        v-if="props.scope === 'franquia' && inheritedPlaceholder('so_additional_service_fiscal_lc116_code')"
        class="inherited-hint"
        data-testid="so-catalog-fiscal-additional-service-inherited-lc116"
      >
        {{ t('Valor atual:') }} {{ inheritedPlaceholder('so_additional_service_fiscal_lc116_code') }}
      </small>
    </div>

    <div class="form-field">
      <input-text
        elem-id="soAdditionalServiceFiscalMunicipalCode"
        v-model="formData.so_additional_service_fiscal_municipal_service_code"
        :label="t('Código do serviço no município')"
        :placeholder="inheritedPlaceholder('so_additional_service_fiscal_municipal_service_code')"
        fluid
        data-testid="so-catalog-fiscal-additional-service-municipal-code-input"
      />
      <small
        v-if="props.scope === 'franquia' && inheritedPlaceholder('so_additional_service_fiscal_municipal_service_code')"
        class="inherited-hint"
        data-testid="so-catalog-fiscal-additional-service-inherited-municipal-code"
      >
        {{ t('Valor atual:') }} {{ inheritedPlaceholder('so_additional_service_fiscal_municipal_service_code') }}
      </small>
    </div>

    <div class="flex-container">
      <div class="flex-item-half">
        <div class="form-field">
          <input-number
            elem-id="soAdditionalServiceFiscalIssAliquota"
            v-model="formData.so_additional_service_fiscal_iss_aliquota"
            :label="t('Alíquota ISS')"
            mode="decimal"
            :minFractionDigits="2"
            :maxFractionDigits="2"
            :min="0"
            :max="100"
            suffix=" %"
            fluid
            data-testid="so-catalog-fiscal-additional-service-iss-aliquota-input"
          />
          <small v-if="errors.iss_aliquota" class="error-message">{{ errors.iss_aliquota }}</small>
          <small
            v-if="props.scope === 'franquia' && inheritedPlaceholder('so_additional_service_fiscal_iss_aliquota')"
            class="inherited-hint"
            data-testid="so-catalog-fiscal-additional-service-inherited-iss-aliquota"
          >
            {{ t('Valor atual:') }} {{ inheritedPlaceholder('so_additional_service_fiscal_iss_aliquota') }}
          </small>
        </div>
      </div>

      <div class="flex-item-half">
        <div class="form-field toggle-field">
          <input-toggle
            elem-id="soAdditionalServiceFiscalIssRetido"
            v-model="formData.so_additional_service_fiscal_iss_retido_default"
            :label="t('ISS retido por padrão')"
            data-testid="so-catalog-fiscal-additional-service-iss-retido-toggle"
          />
        </div>
      </div>
    </div>

    <div class="form-field">
      <input-text
        elem-id="soAdditionalServiceFiscalMunicipioIbge"
        v-model="formData.so_additional_service_fiscal_municipio_incidencia_ibge"
        :label="t('Município de incidência (código IBGE)')"
        :placeholder="inheritedPlaceholder('so_additional_service_fiscal_municipio_incidencia_ibge')"
        fluid
        data-testid="so-catalog-fiscal-additional-service-municipio-ibge-input"
      />
      <small
        v-if="props.scope === 'franquia' && inheritedPlaceholder('so_additional_service_fiscal_municipio_incidencia_ibge')"
        class="inherited-hint"
        data-testid="so-catalog-fiscal-additional-service-inherited-municipio-ibge"
      >
        {{ t('Valor atual:') }} {{ inheritedPlaceholder('so_additional_service_fiscal_municipio_incidencia_ibge') }}
      </small>
    </div>

    <div class="form-field">
      <input-text
        elem-id="soAdditionalServiceFiscalCnae"
        v-model="formData.so_additional_service_fiscal_cnae"
        :label="t('CNAE')"
        :placeholder="inheritedPlaceholder('so_additional_service_fiscal_cnae')"
        fluid
        data-testid="so-catalog-fiscal-additional-service-cnae-input"
      />
      <small
        v-if="props.scope === 'franquia' && inheritedPlaceholder('so_additional_service_fiscal_cnae')"
        class="inherited-hint"
        data-testid="so-catalog-fiscal-additional-service-inherited-cnae"
      >
        {{ t('Valor atual:') }} {{ inheritedPlaceholder('so_additional_service_fiscal_cnae') }}
      </small>
    </div>

    <div class="form-field">
      <input-text
        elem-id="soAdditionalServiceFiscalCodigoTributacaoNacional"
        v-model="formData.so_additional_service_fiscal_codigo_tributacao_nacional"
        :label="t('Código de tributação nacional (NFS-e Nacional)')"
        :placeholder="inheritedPlaceholder('so_additional_service_fiscal_codigo_tributacao_nacional') || t('Ex: 140101')"
        :validation="() => isValidOrEmptyCodigoTributacaoNacional(formData.so_additional_service_fiscal_codigo_tributacao_nacional)"
        fluid
        data-testid="so-catalog-fiscal-additional-service-codigo-tributacao-nacional-input"
      />
      <small v-if="errors.codigo_tributacao_nacional" class="error-message">{{ errors.codigo_tributacao_nacional }}</small>
      <small class="inherited-hint">
        {{ t('Exigido apenas para municípios que usam o padrão NFS-e Nacional (~75% dos habilitados). Deixe em branco se não souber.') }}
      </small>
      <small
        v-if="props.scope === 'franquia' && inheritedPlaceholder('so_additional_service_fiscal_codigo_tributacao_nacional')"
        class="inherited-hint"
        data-testid="so-catalog-fiscal-additional-service-inherited-codigo-tributacao-nacional"
      >
        {{ t('Valor atual:') }} {{ inheritedPlaceholder('so_additional_service_fiscal_codigo_tributacao_nacional') }}
      </small>
    </div>
  </form>
</template>

<script setup lang="ts">
import { reactive, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { emitValidationFailed } from '../../composables/useFormValidation';
import { useSOCatalogFiscal, tinyintToBoolean, type FiscalScope } from '../../composables/useSOCatalogFiscal';
import { isValidLc116Code, isValidCodigoTributacaoNacional } from './fiscalCodeValidation';
import type { So_AdditionalServiceFiscalEffectiveRow } from '../../contracts/So_AdditionalServiceFiscalEffectiveRow';
import { log, error as logError } from '../../utils/DebugLogger';

type Id = number | string;

interface Props {
  itemId?: Id | null;
  franchiseId?: number;
  scope: FiscalScope;
  effective: So_AdditionalServiceFiscalEffectiveRow | null;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  'submit-success': [];
  'submit-error': [error: unknown];
}>();

const { t } = useI18n();
const { salvarAdditionalServiceFiscal } = useSOCatalogFiscal();

// Every field starts untouched (undefined). In the `franquia` scope this
// stays true unless the user types — the omission rule (see the composable
// docblock) depends on it: an untouched field must never reach the payload,
// so it never freezes the inherited global value into the franchise's row.
const formData = reactive<{
  so_additional_service_fiscal_lc116_code?: string;
  so_additional_service_fiscal_municipal_service_code?: string;
  so_additional_service_fiscal_iss_aliquota?: number;
  so_additional_service_fiscal_iss_retido_default?: boolean;
  so_additional_service_fiscal_municipio_incidencia_ibge?: string;
  so_additional_service_fiscal_cnae?: string;
  so_additional_service_fiscal_codigo_tributacao_nacional?: string;
}>({});

const errors = reactive({ lc116_code: '', iss_aliquota: '', codigo_tributacao_nacional: '' });

const clearErrors = (): void => {
  errors.lc116_code = '';
  errors.iss_aliquota = '';
  errors.codigo_tributacao_nacional = '';
};

// Optional at the form level — see SOCatalogFiscalServiceForm's own copy of
// this helper for the full rationale (only required when the município
// resolves to the NFS-e padrao NACIONAL, a fact this form does not know).
const isValidOrEmptyCodigoTributacaoNacional = (code: string | null | undefined): boolean =>
  !code || isValidCodigoTributacaoNacional(code);

/** The value shown as the field's :placeholder in `franquia` scope — never
 *  as the input's value. Mirrors SOCatalogFiscalServiceForm's own
 *  inheritedPlaceholder (see that component's docblock for the full
 *  rationale — same override semantics, same omission rule). */
const inheritedPlaceholder = (
  field: keyof So_AdditionalServiceFiscalEffectiveRow
): string => {
  if (!props.effective) return '';
  const v = props.effective[field];
  return v === undefined || v === null ? '' : String(v);
};

const resetForm = (): void => {
  formData.so_additional_service_fiscal_lc116_code = undefined;
  formData.so_additional_service_fiscal_municipal_service_code = undefined;
  formData.so_additional_service_fiscal_iss_aliquota = undefined;
  formData.so_additional_service_fiscal_iss_retido_default = undefined;
  formData.so_additional_service_fiscal_municipio_incidencia_ibge = undefined;
  formData.so_additional_service_fiscal_cnae = undefined;
  formData.so_additional_service_fiscal_codigo_tributacao_nacional = undefined;
  clearErrors();
};

/** Populate the form from `effective`. In `global` scope these are the
 *  actual values of the row being edited (normal pre-filled edit — no
 *  inheritance involved, so there is nothing to omit). In `franquia` scope
 *  the fields stay blank; `effective` is used only for the placeholders. */
const populateForm = (): void => {
  resetForm();
  if (props.scope === 'global' && props.effective) {
    formData.so_additional_service_fiscal_lc116_code = props.effective.so_additional_service_fiscal_lc116_code;
    formData.so_additional_service_fiscal_municipal_service_code = props.effective.so_additional_service_fiscal_municipal_service_code;
    formData.so_additional_service_fiscal_iss_aliquota =
      props.effective.so_additional_service_fiscal_iss_aliquota != null
        ? Number(props.effective.so_additional_service_fiscal_iss_aliquota)
        : undefined;
    formData.so_additional_service_fiscal_iss_retido_default = tinyintToBoolean(
      props.effective.so_additional_service_fiscal_iss_retido_default
    );
    formData.so_additional_service_fiscal_municipio_incidencia_ibge = props.effective.so_additional_service_fiscal_municipio_incidencia_ibge;
    formData.so_additional_service_fiscal_cnae = props.effective.so_additional_service_fiscal_cnae;
    formData.so_additional_service_fiscal_codigo_tributacao_nacional = props.effective.so_additional_service_fiscal_codigo_tributacao_nacional;
  }
};

watch(() => [props.effective, props.scope, props.itemId], populateForm, { immediate: true });

const validateForm = (): boolean => {
  clearErrors();
  let isValid = true;

  if (!isValidLc116Code(formData.so_additional_service_fiscal_lc116_code)) {
    errors.lc116_code = t('Código LC116 obrigatório, no formato NN.NN (ex: 14.01)');
    isValid = false;
  }

  const aliquota = formData.so_additional_service_fiscal_iss_aliquota;
  if (aliquota != null && (aliquota < 0 || aliquota > 100)) {
    errors.iss_aliquota = t('Alíquota deve estar entre 0 e 100');
    isValid = false;
  }

  if (!isValidOrEmptyCodigoTributacaoNacional(formData.so_additional_service_fiscal_codigo_tributacao_nacional)) {
    errors.codigo_tributacao_nacional = t('Código de tributação nacional deve ter exatamente 6 dígitos (ex: 140101)');
    isValid = false;
  }

  if (!isValid) {
    emitValidationFailed('SOCatalogFiscalAdditionalServiceForm', errors);
    return false;
  }
  return true;
};

const handleSubmit = async (): Promise<void> => {
  log('SOCatalogFiscalAdditionalServiceForm', 'handleSubmit', 'Form submitted');
  if (!validateForm() || props.itemId == null) {
    return;
  }

  try {
    await salvarAdditionalServiceFiscal({
      additionalServiceId: Number(props.itemId),
      scope: props.scope,
      franchiseId: props.franchiseId,
      fields: {
        lc116_code: formData.so_additional_service_fiscal_lc116_code,
        municipal_service_code: formData.so_additional_service_fiscal_municipal_service_code,
        iss_aliquota: formData.so_additional_service_fiscal_iss_aliquota,
        iss_retido_default: formData.so_additional_service_fiscal_iss_retido_default,
        municipio_incidencia_ibge: formData.so_additional_service_fiscal_municipio_incidencia_ibge,
        cnae: formData.so_additional_service_fiscal_cnae,
        codigo_tributacao_nacional: formData.so_additional_service_fiscal_codigo_tributacao_nacional,
      },
    });
    eventBus.emit('globalMessage.success', t('Dados fiscais do serviço adicional salvos com sucesso'));
    emit('submit-success');
  } catch (err: unknown) {
    logError('SOCatalogFiscalAdditionalServiceForm', 'handleSubmit', 'Error', err);
    const message = err instanceof Error ? err.message : '';
    eventBus.emit('globalMessage.error', t('Erro ao salvar dados fiscais do serviço adicional') + ': ' + message);
    emit('submit-error', err);
  }
};

defineExpose({ handleSubmit, validateForm, resetForm });
</script>

<style scoped>
.flex-container {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}

.flex-item-half {
  flex: 1;
  min-width: 0;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
  margin-bottom: 1rem;
}

.toggle-field {
  justify-content: flex-end;
}

.error-message {
  color: var(--p-red-500);
  font-size: 0.875rem;
}

.inherited-hint {
  color: var(--p-text-muted-color);
  font-size: 0.8rem;
}
</style>
